import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, IconButton, Stack, TextField, Typography, useMediaQuery } from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import { AgentMessage, AgentTypingIndicator } from 'src/components/agent/AgentMessage';
import AgentReportPreview from 'src/components/agent/AgentReportPreview';
import { pickExamplePrompts, pickQuickActions } from 'src/components/agent/agentQuickActions';
import { useSorbyIa } from './SorbyIaContext';
import { useAuthContext } from 'src/contexts/auth-context';
import miObraService from 'src/services/miObraService';
import { mensajeDeTarjeta, saludoSorbyIa, TIPO_TARJETA } from 'src/utils/sorbyIa/sorbyIaChat';
import { accionEnCursoVigente, accionesAOfrecer, accionesPendientes, chipsDeAcciones, esSalidaDeAccion, preguntaEleccionVisible, PREGUNTA_ELECCION } from 'src/utils/sorbyIa/recorrido';
import { useAgenteSpecialists } from 'src/hooks/useAgenteSpecialists';
import { avisosDeModulos } from 'src/utils/sorbyIa/modulosPrueba';
import { EVENTO_ELEGIR_ACCION, avisarEstado, avisoDeTransicionParaRol, onboardingParaRol } from 'src/utils/sorbyIa/primerosPasos';
import { CLAVE_CAJA_VISTA, EVENTO_HITO, avisarHito, avisoDeGuia, enlaceDeAccion } from 'src/utils/sorbyIa/guiaPantalla';
import { mensajeDeCaja, pideVerCaja } from 'src/utils/sorbyIa/cajaEnChat';
import { leerMoneda } from './piezasObra';
import { ofertaAMostrar, ofertaNuevaParaRegistrar, esperaTrasResolver, esperaTrasTurno } from 'src/utils/sorbyIa/ofertaModulo';
import { ALTURA_TACTIL } from 'src/components/ui/casa';
import TarjetasArmeEsto from './TarjetasArmeEsto';
import ProximosPasos from './ProximosPasos';
import { proximosPasosAMostrar } from 'src/utils/sorbyIa/proximosPasos';

const MAX_ADJUNTOS = 10;

// Una acción destacada; las demás quedan detrás de «Otras N cosas» para no apilar un botón por paso.
function ChipsAcciones({ chips, disabled = false, onElegir }) {
  const [verOtras, setVerOtras] = useState(false);
  const [principal, ...otras] = chips;
  if (!principal) return null;

  return (
    <Stack spacing={0.5} alignItems="flex-start">
      <Button
        variant="contained"
        disabled={disabled}
        onClick={() => onElegir(principal)}
        sx={{ minHeight: ALTURA_TACTIL, textTransform: 'none', textAlign: 'left', bgcolor: 'neutral.900', '&:hover': { bgcolor: 'neutral.800' } }}
      >
        {principal.label}
      </Button>
      {otras.length > 0 && (
        <Button
          variant="text"
          onClick={() => setVerOtras((v) => !v)}
          aria-expanded={verOtras}
          sx={{ minHeight: ALTURA_TACTIL, textTransform: 'none', fontWeight: 600, px: 0.5 }}
        >
          {verOtras ? 'Ocultar' : otras.length === 1 ? 'Otra cosa' : `Otras ${otras.length} cosas`}
        </Button>
      )}
      {verOtras &&
        otras.map((chip) => (
          <Button
            key={chip.id}
            variant="text"
            disabled={disabled}
            onClick={() => onElegir(chip)}
            sx={{ minHeight: ALTURA_TACTIL, textTransform: 'none', textAlign: 'left', justifyContent: 'flex-start', px: 0.5 }}
          >
            {chip.label}
          </Button>
        ))}
    </Stack>
  );
}

export default function ChatSorbyIa({ compacto = false }) {
  const router = useRouter();
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const archivoRef = useRef(null);
  const [borrador, setBorrador] = useState('');
  const [adjuntos, setAdjuntos] = useState([]);
  const [miObra, setMiObra] = useState(null);
  const [enCurso, setEnCurso] = useState(null);
  const [avisos, setAvisos] = useState([]);
  const [espera, setEspera] = useState(0);
  const [mostrarEnlaceCaja, setMostrarEnlaceCaja] = useState(false);
  const [verReporte, setVerReporte] = useState(false);
  const [proximoElegido, setProximoElegido] = useState(false);
  const reporteYaAbierto = useRef(false);
  const pantallaChica = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const ofertasRegistradas = useRef([]);
  const miObraRef = useRef(null);
  const enCursoRef = useRef(null);
  const estabaEnviando = useRef(false);
  const cantidadMensajes = useRef(0);
  const { user } = useAuthContext();
  const {
    messages,
    isLoadingHistory,
    isSending,
    awaitingConfirm,
    suggestions,
    reportDraft,
    error,
    hasLoadedHistory,
    loadHistory,
    sendMessage: enviarAlAgente,
    confirmCurrent,
    cancelCurrent,
    dismissError,
  } = useSorbyIa().chat;
  const sendMessage = useCallback(
    (texto, archivos) => {
      if (esSalidaDeAccion(texto)) setEnCurso(null);
      return enviarAlAgente(texto, archivos);
    },
    [enviarAlAgente],
  );
  const cancelar = () => {
    setEnCurso(null);
    cancelCurrent();
  };
  const { specialists } = useAgenteSpecialists();
  const accionesRapidas = useMemo(() => pickQuickActions(specialists), [specialists]);
  const ejemplos = useMemo(() => pickExamplePrompts(specialists), [specialists]);
  const puedeAdjuntar = !!specialists?.corralon || !!specialists?.reportes;

  useEffect(() => {
    if (!hasLoadedHistory) loadHistory();
  }, [hasLoadedHistory, loadHistory]);

  useEffect(() => {
    let vigente = true;
    miObraService
      .obtener()
      .then((respuesta) => vigente && setMiObra(respuesta))
      .catch((err) => console.error('[sorbyIa] saludo.lectura_fallida', err));
    return () => {
      vigente = false;
    };
  }, []);

  miObraRef.current = miObra;
  enCursoRef.current = enCurso;
  cantidadMensajes.current = messages.length;

  const releerEstado = useCallback((calcularAviso, esVigente) => {
    miObraService
      .obtener()
      .then((nueva) => {
        if (!esVigente()) return;
        const aviso = calcularAviso(miObraRef.current?.onboarding, nueva?.onboarding, nueva?.es_dueno === true);
        setMiObra(nueva);
        avisarEstado(nueva);
        if (aviso) {
          setEnCurso(null);
          setAvisos((prev) => [...prev, { ...aviso, id: `${Date.now()}-${prev.length}`, despuesDe: cantidadMensajes.current }]);
        }
      })
      .catch((err) => console.error('[sorbyIa] estado.relectura_fallida', err));
  }, []);

  // Después de cada respuesta del agente se relee el estado: si una acción pasó a completa, el chat lo celebra y vuelve a preguntar.
  useEffect(() => {
    if (isSending) {
      estabaEnviando.current = true;
      return undefined;
    }
    if (!estabaEnviando.current) return undefined;
    estabaEnviando.current = false;
    setEspera(esperaTrasTurno);
    let vigente = true;
    releerEstado(avisoDeTransicionParaRol, () => vigente);
    return () => {
      vigente = false;
    };
  }, [isSending, releerEstado]);

  // Un hito (p. ej. abrir la caja) no viene de una respuesta del agente: se relee el estado y, si la persona venía de elegir esa acción, la IA comenta la pantalla.
  useEffect(() => {
    let vigente = true;
    const alHito = (evento) => {
      releerEstado(
        (previo, actual, esDueno) =>
          avisoDeGuia({ clave: evento.detail?.clave, enCurso: enCursoRef.current, actual: onboardingParaRol(actual, esDueno), ruta: router.pathname }) ||
          avisoDeTransicionParaRol(previo, actual, esDueno),
        () => vigente,
      );
    };
    window.addEventListener(EVENTO_HITO, alHito);
    return () => {
      vigente = false;
      window.removeEventListener(EVENTO_HITO, alHito);
    };
  }, [router.pathname, releerEstado]);

  const oferta = ofertaAMostrar({ miObra, espera, ocupado: isSending || awaitingConfirm });
  const moduloOferta = oferta?.payload?.modulo;

  useEffect(() => {
    const modulo = ofertaNuevaParaRegistrar(oferta, ofertasRegistradas.current);
    if (!modulo) return;
    ofertasRegistradas.current = [...ofertasRegistradas.current, modulo];
    miObraService.registrarOferta(modulo, 'mostrada').catch((err) => console.error('[sorbyIa] oferta.registro_fallido', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduloOferta]);

  useEffect(() => setMostrarEnlaceCaja(false), [messages.length]);

  // El reporte se abre solo la primera vez que aparece un borrador; después queda el botón para volver a verlo.
  useEffect(() => {
    if (!reportDraft) {
      reporteYaAbierto.current = false;
      return;
    }
    if (!reporteYaAbierto.current) {
      reporteYaAbierto.current = true;
      setVerReporte(true);
    }
  }, [reportDraft]);

  const guardarReporte = useCallback(
    (nombre) => {
      setVerReporte(false);
      sendMessage(`Guardá este reporte con el nombre "${nombre}".`);
    },
    [sendMessage],
  );
  const cerrarReporte = useCallback(() => setVerReporte(false), []);
  // Volver al chat para pedir cambios: se cierra el reporte y el cursor queda listo para escribir.
  const pedirCambiosAlReporte = useCallback(() => {
    setVerReporte(false);
    setTimeout(() => inputRef.current?.focus(), 150);
  }, []);

  useEffect(() => {
    const nodo = scrollRef.current;
    if (nodo) nodo.scrollTop = nodo.scrollHeight;
  }, [messages.length, avisos.length, isSending, moduloOferta]);

  // Al abrir el teclado la hoja se achica y el último mensaje quedaba tapado: si la persona estaba al final, se acompaña.
  const pegadoAbajo = useRef(true);
  const alScrollear = useCallback((e) => {
    const n = e.currentTarget;
    pegadoAbajo.current = n.scrollHeight - n.scrollTop - n.clientHeight < 80;
  }, []);
  const irAlFinal = useCallback(() => {
    const nodo = scrollRef.current;
    if (nodo) nodo.scrollTop = nodo.scrollHeight;
  }, []);
  useEffect(() => {
    const nodo = scrollRef.current;
    if (!nodo || typeof ResizeObserver === 'undefined') return undefined;
    const observador = new ResizeObserver(() => { if (pegadoAbajo.current) irAlFinal(); });
    observador.observe(nodo);
    return () => observador.disconnect();
  }, [irAlFinal]);

  const mostrarCajaRef = useRef(null);

  const enviar = useCallback(() => {
    const texto = borrador.trim();
    if ((!texto && adjuntos.length === 0) || isSending) return;
    setBorrador('');
    if (adjuntos.length === 0 && miObraRef.current && pideVerCaja(texto)) {
      mostrarCajaRef.current({ mensaje: texto });
      return;
    }
    const archivos = adjuntos;
    setAdjuntos([]);
    sendMessage(texto, archivos);
  }, [borrador, adjuntos, isSending, sendMessage]);

  const alTeclear = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  const alElegirArchivos = (e) => {
    const elegidos = Array.from(e.target.files || []);
    if (elegidos.length) setAdjuntos((prev) => [...prev, ...elegidos].slice(0, MAX_ADJUNTOS));
    e.target.value = '';
  };

  const alAccionRapida = (accion) => {
    if (accion.autosend) {
      if (!isSending) sendMessage(accion.prefill);
      return;
    }
    setBorrador(accion.prefill || '');
    inputRef.current?.focus();
  };

  // «Ver la caja» se responde acá con los saldos y los últimos movimientos; el modelo no interviene.
  const mostrarCaja = (chip) => {
    const obras = miObraRef.current?.obras || [];
    const pendiente = accionesPendientes(onboardingParaRol(miObraRef.current?.onboarding, miObraRef.current?.es_dueno === true)).some((p) => p.clave === CLAVE_CAJA_VISTA);
    const despuesDe = cantidadMensajes.current;
    const nuevos = [
      { rol: 'user', texto: chip.mensaje },
      { rol: 'assistant', texto: mensajeDeCaja(obras, leerMoneda()) },
    ];
    setAvisos((prev) => [...prev, ...nuevos.map((a, i) => ({ ...a, id: `caja-${Date.now()}-${i}`, despuesDe }))]);
    setMostrarEnlaceCaja(true);
    if (pendiente) {
      miObraService
        .registrarHito(CLAVE_CAJA_VISTA)
        .then(() => avisarHito(CLAVE_CAJA_VISTA))
        .catch((err) => console.error('[sorbyIa] hito.registro_fallido', err));
    }
  };

  mostrarCajaRef.current = mostrarCaja;

  const elegirAccion = (chip) => {
    if (isSending) return;
    if (chip.id === CLAVE_CAJA_VISTA && miObraRef.current) {
      mostrarCaja(chip);
      return;
    }
    setEnCurso(chip.id);
    sendMessage(chip.mensaje);
  };

  // La card «Primeros pasos» pide una acción con el mismo gesto que un chip.
  const elegirAccionRef = useRef(elegirAccion);
  elegirAccionRef.current = elegirAccion;
  useEffect(() => {
    const alPedir = (evento) => {
      const { clave, titulo } = evento.detail || {};
      if (clave && titulo) elegirAccionRef.current({ id: clave, mensaje: titulo });
    };
    window.addEventListener(EVENTO_ELEGIR_ACCION, alPedir);
    return () => window.removeEventListener(EVENTO_ELEGIR_ACCION, alPedir);
  }, []);

  const alAccionDeMensaje = useCallback(
    (accion) => {
      if (!accion?.type) return;
      if ((accion.type === 'open_report' || accion.type === 'open_presupuesto_profesional') && accion.url) {
        router.push(accion.url);
      } else if (accion.type === TIPO_TARJETA) {
        sendMessage(mensajeDeTarjeta(accion));
      } else if ((accion.type === 'abrir_alta_obra' || accion.type === 'open_obra') && accion.url) {
        router.push(accion.url);
      } else if (accion.type === 'suggest_create_report') {
        sendMessage('guardar como reporte');
      } else if ((accion.type === 'view_in_caja' || accion.type === 'agendar_reunion') && accion.url) {
        window.open(accion.url, '_blank', 'noopener,noreferrer');
      }
    },
    [router, sendMessage],
  );

  const resolverOferta = (accion, resultado) => {
    const modulo = accion?.payload?.modulo;
    if (!modulo) return;
    setEspera(esperaTrasResolver(resultado));
    setMiObra((prev) => (prev ? { ...prev, oferta: null } : prev));
    miObraService.registrarOferta(modulo, resultado).catch((err) => console.error('[sorbyIa] oferta.registro_fallido', err));
  };

  const aceptarOferta = (accion) => {
    setMiObra((prev) => (prev ? { ...prev, oferta: null } : prev));
    sendMessage(mensajeDeTarjeta(accion));
  };

  const opcionesProximas = proximosPasosAMostrar({ miObra, ocupado: isSending || awaitingConfirm, elegidoLocal: proximoElegido });
  const elegirProximoPaso = (opcion) => {
    setProximoElegido(true);
    sendMessage(opcion.titulo);
  };
  const bloqueProximos = opcionesProximas ? <ProximosPasos opciones={opcionesProximas} onElegir={elegirProximoPaso} /> : null;
  const bloqueOferta = oferta ? <TarjetasArmeEsto tarjetas={[oferta]} onAction={aceptarOferta} onRechazar={(a) => resolverOferta(a, 'rechazada')} /> : null;

  const claveEnlace = enCurso || (mostrarEnlaceCaja ? CLAVE_CAJA_VISTA : null);
  const enlaceAccion = !isSending && claveEnlace ? enlaceDeAccion(claveEnlace, miObra?.empresa?.id) : null;
  const avisosEn = (posicion) =>
    avisos
      .filter((a) => a.despuesDe === posicion)
      .map((a) => (
        <Box key={a.id}>
          <AgentMessage role={a.rol || 'assistant'} content={a.texto} />
        </Box>
      ));

  const sinMensajes = !isLoadingHistory && messages.length === 0 && avisos.length === 0 && !isSending;
  const enRecorrido = miObra?.onboarding?.activo === true;
  const accionesInicio = enRecorrido ? [] : accionesRapidas.slice(0, 3);
  const ejemplosInicio = enRecorrido ? [] : ejemplos.slice(0, 3);
  const onboardingDelRol = onboardingParaRol(miObra?.onboarding, miObra?.es_dueno === true);
  const pendientes = accionesPendientes(onboardingDelRol);
  // Un borrador de reporte es la acción de reporte en curso aunque la pantalla se haya recargado y no lo recuerde.
  const accionEnCursoEfectiva = enCurso || (reportDraft ? 'reporte_creado' : null);
  const chipsAcciones = chipsDeAcciones(accionesAOfrecer(onboardingDelRol, accionEnCursoEfectiva));
  const mostrarPregunta = preguntaEleccionVisible({ onboarding: onboardingDelRol, enCurso: accionEnCursoEfectiva, awaitingConfirm, isSending });
  const mostrarPedirOtraCosa = !!accionEnCursoVigente(accionEnCursoEfectiva, pendientes) && !isSending && !awaitingConfirm;
  const avisosModulos = avisosDeModulos(miObra?.modulos);

  return (
    <Box
      component="section"
      aria-label="Chat con Sorby IA"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...(compacto
          ? { flex: 1, minHeight: 0, borderTop: 1 }
          : {
              height: { xs: 'calc(100dvh - 140px)', md: 'calc(100dvh - 120px)' },
              minHeight: 420,
              border: 1,
              borderRadius: 1,
            }),
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {reportDraft ? (
        <Dialog fullScreen={pantallaChica} fullWidth maxWidth="lg" open={verReporte} onClose={cerrarReporte}>
          <AgentReportPreview draft={reportDraft} user={user} onSave={guardarReporte} onClose={cerrarReporte} onPedirCambios={pedirCambiosAlReporte} />
        </Dialog>
      ) : null}

      {error ? (
        <Alert severity="warning" onClose={dismissError} sx={{ borderRadius: 0 }}>
          {error}
        </Alert>
      ) : null}

      <Box ref={scrollRef} onScroll={alScrollear} sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, sm: 3 }, py: 2 }}>
        {isLoadingHistory ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : sinMensajes ? (
          <Stack spacing={2} sx={{ py: { xs: 2, sm: 4 }, maxWidth: 560, mx: 'auto' }}>
            {avisosModulos.map((aviso) => (
              <Typography key={aviso} variant="body2" sx={{ fontWeight: 600 }}>
                {aviso}
              </Typography>
            ))}
            {mostrarPregunta ? (
              <Typography variant="body1" component="h2">
                {saludoSorbyIa({ nombre: user?.firstName, onboarding: miObra?.onboarding })}
              </Typography>
            ) : (
              <>
                <Typography variant="h5" component="h2">
                  ¿En qué te puedo ayudar?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cargá un gasto, armá un presupuesto o consultá cómo viene tu obra. Escribilo como se lo dirías a una persona.
                </Typography>
              </>
            )}
            {accionesInicio.length > 0 ? (
              <Stack direction="row" useFlexGap spacing={1} flexWrap="wrap">
                {accionesInicio.map((accion) => (
                  <Button
                    key={accion.id}
                    variant="outlined"
                    disabled={isSending}
                    onClick={() => alAccionRapida(accion)}
                    sx={{ minHeight: ALTURA_TACTIL, textTransform: 'none' }}
                  >
                    {accion.label}
                  </Button>
                ))}
              </Stack>
            ) : null}
            {ejemplosInicio.length > 0 ? (
              <Stack spacing={0.5}>
                {ejemplosInicio.map((ejemplo) => (
                  <Button
                    key={ejemplo.id}
                    disabled={isSending}
                    onClick={() => sendMessage(ejemplo.text)}
                    sx={{ minHeight: ALTURA_TACTIL, justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none' }}
                  >
                    {ejemplo.text}
                  </Button>
                ))}
              </Stack>
            ) : null}
            {bloqueOferta}
            {bloqueProximos}
          </Stack>
        ) : (
          <>
            {avisosEn(0)}
            {messages.map((m, idx) => (
              <Fragment key={`${m.role}-${idx}-${m.createdAt ?? ''}`}>
                <AgentMessage
                  role={m.role}
                  content={m.content}
                  createdAt={m.createdAt}
                  actions={m.actions}
                  attachments={m.attachments}
                  onAction={alAccionDeMensaje}
                />
                {avisosEn(idx + 1)}
              </Fragment>
            ))}
            {isSending ? <AgentTypingIndicator label={null} /> : null}
            {bloqueOferta || bloqueProximos ? <Box sx={{ pb: 1.5 }}>{bloqueOferta}{bloqueProximos}</Box> : null}
            {enlaceAccion ? (
              <Box sx={{ pb: 1.5 }}>
                <Button variant="text" onClick={() => router.push(enlaceAccion.ruta)} sx={{ minHeight: ALTURA_TACTIL, textTransform: 'none', fontWeight: 600, px: 0.5 }}>
                  {enlaceAccion.etiqueta}
                </Button>
              </Box>
            ) : null}
          </>
        )}
      </Box>

      {mostrarPregunta || mostrarPedirOtraCosa ? (
        <Stack spacing={1} sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
          {mostrarPregunta ? (
            <>
              <Typography variant="subtitle1" component="p">
                {PREGUNTA_ELECCION}
              </Typography>
              <ChipsAcciones chips={chipsAcciones} onElegir={elegirAccion} />
            </>
          ) : (
            <Button color="inherit" onClick={() => sendMessage('otra cosa')} sx={{ alignSelf: 'flex-start', minHeight: ALTURA_TACTIL, textTransform: 'none' }}>
              Pedir otra cosa
            </Button>
          )}
        </Stack>
      ) : null}

      {awaitingConfirm && !isSending ? (
        <Stack direction="row" spacing={1} sx={{ px: { xs: 2, sm: 3 }, py: 1, borderTop: 1, borderColor: 'divider' }}>
          <Button variant="contained" onClick={confirmCurrent} sx={{ minHeight: ALTURA_TACTIL }}>
            Confirmar
          </Button>
          <Button variant="outlined" onClick={cancelar} sx={{ minHeight: ALTURA_TACTIL }}>
            Cancelar
          </Button>
        </Stack>
      ) : null}

      {suggestions.length > 0 && !awaitingConfirm && !isSending ? (
        <Stack
          direction="row"
          spacing={1}
          sx={{ px: { xs: 2, sm: 3 }, pt: 1, overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
        >
          {suggestions.map((chip, i) => (
            <Chip
              key={`${chip.label}-${i}`}
              label={chip.label}
              variant="outlined"
              onClick={() => chip?.message && sendMessage(chip.message)}
              sx={{ flexShrink: 0, height: ALTURA_TACTIL }}
            />
          ))}
        </Stack>
      ) : null}

      <Box sx={{ px: { xs: 1.5, sm: 3 }, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
        {adjuntos.length > 0 ? (
          <Stack direction="row" useFlexGap spacing={0.75} flexWrap="wrap" sx={{ mb: 1 }}>
            {adjuntos.map((f, i) => (
              <Chip
                key={`${f.name}-${i}`}
                size="small"
                label={f.name}
                disabled={isSending}
                onDelete={() => setAdjuntos((prev) => prev.filter((_, j) => j !== i))}
              />
            ))}
          </Stack>
        ) : null}
        <input ref={archivoRef} type="file" accept="image/*,application/pdf" multiple hidden onChange={alElegirArchivos} />
        <Stack direction="row" spacing={1} alignItems="flex-end">
          {puedeAdjuntar ? (
            <IconButton
              aria-label="Adjuntar archivo"
              disabled={isSending}
              onClick={() => archivoRef.current?.click()}
              sx={{ width: ALTURA_TACTIL, height: ALTURA_TACTIL }}
            >
              <AttachFileRoundedIcon />
            </IconButton>
          ) : null}
          <TextField
            inputRef={inputRef}
            value={borrador}
            onChange={(e) => setBorrador(e.target.value)}
            onKeyDown={alTeclear}
            onFocus={() => {
              // El teclado tarda en abrir: se espera a que la hoja termine de achicarse.
              pegadoAbajo.current = true;
              setTimeout(irAlFinal, 350);
            }}
            placeholder="Escribí lo que necesitás"
            fullWidth
            multiline
            maxRows={5}
            size="small"
            disabled={isSending}
          />
          <IconButton
            aria-label="Enviar"
            color="primary"
            onClick={enviar}
            disabled={isSending || (!borrador.trim() && adjuntos.length === 0)}
            sx={{ width: ALTURA_TACTIL, height: ALTURA_TACTIL }}
          >
            {isSending ? <CircularProgress size={18} /> : <SendRoundedIcon />}
          </IconButton>
        </Stack>
      </Box>
    </Box>
  );
}
